import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { BookPlusIcon, EyeOffIcon, LoaderCircleIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getSpellingSuggestions, ignoreSpellingWord, spellingIssueAt, type SpellingIssue } from './document-spell-check';

type MenuState = { open: false } | { open: true; issue: SpellingIssue; x: number; y: number };
type SuggestionState = { status: 'loading' } | { status: 'ready'; items: string[] };

/**
 * Right-click (or the keyboard context-menu key) on an underlined word opens a menu of corrections,
 * Ignore and Add to dictionary. It runs in the capture phase so other editor menus skip handled events.
 */
export function SpellingContextMenu({ editor, readOnly, onAddToDictionary }: {
  editor: Editor;
  readOnly: boolean;
  /** Saves the word to the signed-in person's own dictionary; omitted when nobody is signed in. */
  onAddToDictionary?: (word: string) => void;
}) {
  const [menu, setMenu] = React.useState<MenuState>({ open: false });
  const [suggestions, setSuggestions] = React.useState<SuggestionState>({ status: 'loading' });

  React.useEffect(() => {
    if (readOnly || editor.isDestroyed) return;
    const dom = editor.view.dom;
    const onContextMenu = (event: MouseEvent) => {
      // AI suggestions have their own menu.
      if (event.target instanceof Element && event.target.closest('.ai-proofing-issue')) return;
      let x = event.clientX;
      let y = event.clientY;
      let issue: SpellingIssue | null = null;
      const element = event.target instanceof Element ? event.target.closest('.spell-error') : null;
      if (element && dom.contains(element)) issue = spellingIssueAt(editor.state, editor.view.posAtDOM(element, 0));
      if (!issue && event.button !== 2 && x === 0 && y === 0) {
        // Keyboard-opened menus have no pointer position: use the caret instead.
        issue = spellingIssueAt(editor.state, editor.state.selection.from);
        if (issue) {
          const coords = editor.view.coordsAtPos(issue.to);
          x = coords.left;
          y = coords.bottom;
        }
      }
      if (!issue) return;
      event.preventDefault();
      setSuggestions({ status: 'loading' });
      setMenu({ open: true, issue, x, y });
    };
    dom.addEventListener('contextmenu', onContextMenu, true);
    return () => dom.removeEventListener('contextmenu', onContextMenu, true);
  }, [editor, readOnly]);

  React.useEffect(() => { if (readOnly) setMenu({ open: false }); }, [readOnly]);

  React.useEffect(() => {
    if (!menu.open) return;
    let cancelled = false;
    getSpellingSuggestions(editor, menu.issue.word)
      .then(items => { if (!cancelled) setSuggestions({ status: 'ready', items }); })
      .catch(() => { if (!cancelled) setSuggestions({ status: 'ready', items: [] }); });
    return () => { cancelled = true; };
  }, [editor, menu]);

  if (!menu.open) return null;
  const { issue } = menu;
  const close = () => setMenu({ open: false });
  const replace = (replacement: string) => {
    // Live editing can move the word while the menu is open; use where it is now.
    const current = spellingIssueAt(editor.state, issue.from) ?? issue;
    editor.chain().focus().replaceMisspelling(current, replacement).run();
    close();
  };
  const ignore = () => { ignoreSpellingWord(issue.word); editor.commands.focus(); close(); };
  const addToDictionary = () => { onAddToDictionary?.(issue.word); editor.commands.focus(); close(); };

  return <DropdownMenu open onOpenChange={open => { if (!open) close(); }} modal={false}>
    <DropdownMenuTrigger asChild>
      <span aria-hidden="true" className="pointer-events-none fixed size-0" style={{ left: menu.x, top: menu.y }} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" side="bottom" sideOffset={2} className="w-60" aria-label={`Spelling suggestions for ${issue.word}`}>
      {suggestions.status === 'loading'
        ? <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground"><LoaderCircleIcon className="size-4 animate-spin" aria-hidden /> Finding suggestions…</DropdownMenuLabel>
        : suggestions.items.length
          ? suggestions.items.map(item => <DropdownMenuItem key={item} className="font-medium" onSelect={() => replace(item)}>{item}</DropdownMenuItem>)
          : <DropdownMenuLabel className="font-normal text-muted-foreground">No suggestions</DropdownMenuLabel>}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={ignore}><EyeOffIcon aria-hidden /> Ignore</DropdownMenuItem>
      {onAddToDictionary && <DropdownMenuItem onSelect={addToDictionary}><BookPlusIcon aria-hidden /> Add to dictionary</DropdownMenuItem>}
    </DropdownMenuContent>
  </DropdownMenu>;
}
